export class SwiperConfigurator {
  static swiperInstances = new Map();

  /**
   * @param {HTMLElement} sliderElement - The DOM element containing the slider.
   */
  constructor(sliderElement) {
    this.element = sliderElement;
    this.options = this.parseConfig();    
    this.setupEventListeners();

    if (this.options.observer) {
      this.initIntersectionObserver();
    } else {
      this.initializeSwiper();
    }
  }

  parseConfig() {
    const {
      dataset: { config, mobile, observer, id },
    } = this.element;

    try {
      if (typeof config !== 'string') {
        throw new Error(`Expected "config" to be a string, but received ${typeof config}`);
      }
      return {
        config: JSON.parse(config),
        mobile: mobile === 'true',
        observer: observer === 'true',
        ...(id && { id }),
      };
    } catch (error) {
      console.error('Error parsing slider configuration:', error);
      return { config: {}, mobile: false, observer: false, ...(id && { id }) };
    }
  }

  initializeSwiper() {
    if (this.shouldDisableSwiper()) {
      return;
    }
    // @ts-ignore
    if (!Swiper || !this.element || this.element.swiper) {
      return;
    }
    if (this.options.config.thumbs?.swiper) {
      const thumbsSelector = this.options.config.thumbs.swiper;
      const thumbsElement = document.querySelector(thumbsSelector);
      const thumbsInstance = SwiperConfigurator.swiperInstances.get(thumbsElement);

      if (!thumbsInstance) {
        setTimeout(() => this.initializeSwiper(), 0);
        return;
      }

      this.options.config.thumbs.swiper = thumbsInstance;
    }
    // @ts-ignore
    const instance = new Swiper(this.element, this.options.config);
  }

  shouldDisableSwiper() {
    return this.options.mobile && window.innerWidth > 768;
  }

  setupEventListeners() {
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleResize() {
    if (this.shouldDisableSwiper()) {
      this.destroySwiper();
    } else {
      this.initializeSwiper();
    }
  }

  destroySwiper() {
    // @ts-ignore
    if (this.element && this.element.swiper) {
      // @ts-ignore
      this.element.swiper.destroy(true, true);
    }
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log('Element is in view, initializing swiper');
            this.observer.disconnect();
            this.initializeSwiper();
          }
        });
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this.element);
  }

  /**
   * @param {'script' | 'style'} tagType - The type of tag to append.
   * @param {string} filePath - The path to the file.
   * @returns {Promise<void>} Resolves when the file is successfully loaded.
   */
  static appendTagToBody(tagType, filePath) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement(tagType === 'script' ? 'script' : 'link');

      if (tagType === 'script' && tag instanceof HTMLScriptElement) {
        tag.src = filePath;
        tag.type = 'text/javascript';
      } else if (tagType === 'style' && tag instanceof HTMLLinkElement) {
        tag.href = filePath;
        tag.rel = 'stylesheet';
      } else {
        return reject(new Error('Unsupported tag type. Please use "script" or "style".'));
      }

      tag.onload = () => resolve();
      tag.onerror = () => reject(new Error(`Failed to load ${tagType} file: ${filePath}`));

      document.body.appendChild(tag);
    });
  }

  static appendSwiperFiles() {
    if (Theme.swiperFile.insertFiles) {
      return Promise.resolve();
    }

    const { css, js } = Theme.swiperFile;
    const files = [
      { type: 'style', path: css },
      { type: 'script', path: js },
    ].filter((file) => file.path);

    if (files.length === 0) {
      return Promise.resolve();
    }

    const loadFilePromises = files.map((file) =>
      SwiperConfigurator.appendTagToBody(/** @type {'script' | 'style'} */ (file.type), file.path)
    );

    return Promise.all(loadFilePromises).then(() => {
      Theme.swiperFile.insertFiles = true;
    });
  }
}

const sliderElements = document.querySelectorAll('[data-slider-selector]');

if (sliderElements.length > 0) {
  SwiperConfigurator.appendSwiperFiles()
    .then(() => {
      sliderElements.forEach((slider) => {
        if (slider instanceof HTMLElement) {
          new SwiperConfigurator(slider);
        }
      });
    })
    .catch((error) => {
      console.error('Failed to load Swiper files:', error);
    })
}
